import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Building2,
  Sun,
  Utensils,
  FolderLock,
  Wrench,
  Sparkles,
  ArrowUpRight,
  X,
  Check,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import useSeo from "@/lib/useSeo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECTS = [
  {
    id: "estate-agent-lead-tracker",
    icon: Building2,
    title: "Estate Agent Lead Tracker",
    problem:
      "Leads arriving from multiple sources and falling through the cracks.",
    solution:
      "Lead pipeline, valuation requests, automated follow-up and reporting.",
    range: "£995 – £1,495",
  },
  {
    id: "solar-sales-crm",
    icon: Sun,
    title: "Solar Sales CRM",
    problem:
      "Sales teams spending too much time chasing poor quality enquiries.",
    solution:
      "Lead qualification, survey booking, customer tracking and sales pipeline management.",
    range: "£1,495 – £2,495",
  },
  {
    id: "restaurant-loyalty-platform",
    icon: Utensils,
    title: "Restaurant Loyalty Platform",
    problem: "Customers visit once and never return.",
    solution:
      "Review generation, customer database, email campaigns and repeat visit marketing.",
    range: "£795 – £1,295",
  },
  {
    id: "client-portal",
    icon: FolderLock,
    title: "Client Portal",
    problem:
      "Too much back-and-forth communication and document chasing.",
    solution:
      "Private client area, document sharing, project tracking and communication hub.",
    range: "£995 – £2,995",
  },
  {
    id: "trades-business-automation",
    icon: Wrench,
    title: "Trades Business Automation",
    problem: "Missed enquiries and excessive admin work.",
    solution:
      "Lead capture, automated follow-up, quote management and customer tracking.",
    range: "£795 – £1,995",
  },
  {
    id: "luxury-concierge-platform",
    icon: Sparkles,
    title: "Luxury Concierge Platform",
    problem:
      "Information spread across emails, WhatsApp and spreadsheets.",
    solution:
      "Guest onboarding, preferences, requests, communications and operational coordination.",
    range: "£1,250 – £2,500",
  },
];

const PAIN_PROMPTS = [
  "Losing leads",
  "Too much administration",
  "Endless emails",
  "Poor communication",
  "Manual processes",
  "Too many spreadsheets",
];

export default function WhatCouldWeBuild() {
  useSeo({
    title:
      "What Could We Build For Your Business? | Linked2Marketing",
    description:
      "Most businesses don't need more software — they need the right systems for their problem. See real examples and typical project ranges, often a fraction of what most owners expect.",
    canonical: "https://linked2marketing.com/what-could-we-build",
    ogType: "website",
  });

  return (
    <div
      data-testid="page-what-could-we-build"
      className="min-h-screen bg-[#07090D] text-mute-100"
    >
      <Navbar />
      <main className="pt-28 sm:pt-32">
        <Hero />
        <ProjectsSection />
        <ComparisonSection />
        <BottomFormSection />
      </main>
      <Footer />
    </div>
  );
}

/* ---------------- Hero ---------------- */
function Hero() {
  return (
    <section
      data-testid="wcwb-hero"
      className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
    >
      <div className="absolute inset-0 -z-10 pointer-events-none overflow-hidden">
        <div
          className="absolute left-1/2 -translate-x-1/2 top-[-160px] w-[1200px] h-[700px] opacity-70 blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
          }}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <div className="lg:col-span-8">
          <span className="eyebrow text-gold">— Most businesses don&rsquo;t need more software</span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[68px] leading-[1.04] tracking-tight">
            What Could We Build For{" "}
            <em className="not-italic text-gradient-gold">Your Business</em>?
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p className="text-[16.5px] leading-[1.75] text-mute-200">
            Many business owners assume bespoke systems cost tens of thousands of pounds.
          </p>
          <p className="mt-5 text-[15.5px] leading-[1.7] text-mute-200">
            In reality, modern development tools allow practical business solutions to be built
            faster and more affordably than ever before.
          </p>
          <p className="mt-5 text-[15px] leading-[1.7] text-mute-300">
            We focus on solving business problems, not creating unnecessary complexity.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Projects ---------------- */
function ProjectsSection() {
  return (
    <section
      id="examples"
      data-testid="wcwb-projects"
      className="relative mt-24 sm:mt-36 scroll-mt-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <span className="eyebrow text-gold">— Real problems. Real solutions.</span>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-[48px] leading-[1.08] tracking-tight text-mute-100">
              Examples of the systems we regularly design and build for growing businesses.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[15.5px] leading-[1.75] text-mute-200">
              Each one started the same way — a frustration, a bottleneck, a place where a business
              owner kept thinking <span className="text-mute-100 italic">&ldquo;there has to be a
              better way to handle this.&rdquo;</span>
            </p>
          </div>
        </div>
      </div>

      {/* Desktop grid */}
      <div className="hidden md:block mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROJECTS.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      </div>

      {/* Mobile horizontal scroll carousel */}
      <div
        data-testid="wcwb-projects-mobile-carousel"
        className="md:hidden mt-12 -mx-4 px-4 overflow-x-auto snap-x snap-mandatory flex gap-4 pb-6"
        style={{ scrollbarWidth: "none" }}
      >
        <style>{`
          [data-testid="wcwb-projects-mobile-carousel"]::-webkit-scrollbar { display: none; }
        `}</style>
        {PROJECTS.map((p) => (
          <div key={p.id} className="snap-start flex-none w-[85%]">
            <ProjectCard project={p} />
          </div>
        ))}
      </div>

      {/* Mobile swipe hint */}
      <div className="md:hidden mx-auto max-w-7xl px-4 mt-1">
        <p className="text-[11px] uppercase tracking-[0.22em] text-mute-300">
          Swipe for more examples →
        </p>
      </div>
    </section>
  );
}

function ProjectCard({ project }) {
  const Icon = project.icon;
  return (
    <article
      data-testid={`wcwb-project-${project.id}`}
      className="group relative rounded-3xl glass p-7 sm:p-8 h-full flex flex-col overflow-hidden transition-colors duration-500 hover:border-gold/25"
    >
      <div
        className="absolute -top-24 -right-24 w-[280px] h-[280px] rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          background:
            "radial-gradient(closest-side, rgba(199,166,106,0.10), transparent 70%)",
        }}
      />
      <div className="relative flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gold/25 bg-gold/[0.05] text-gold">
          <Icon className="w-[18px] h-[18px]" />
        </span>
      </div>

      <h3 className="relative mt-6 font-display text-[26px] leading-[1.18] text-mute-100">
        {project.title}
      </h3>

      <dl className="relative mt-6 space-y-5 text-[14.5px] leading-[1.7]">
        <div>
          <dt className="text-[10.5px] uppercase tracking-[0.22em] text-mute-300">
            Problem
          </dt>
          <dd className="mt-2 text-mute-200">{project.problem}</dd>
        </div>
        <div>
          <dt className="text-[10.5px] uppercase tracking-[0.22em] text-mute-300">
            Solution
          </dt>
          <dd className="mt-2 text-mute-200">{project.solution}</dd>
        </div>
      </dl>

      <div className="relative mt-auto pt-7">
        <div className="hairline mb-5" />
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-mute-300">
          Typical Project Range
        </p>
        <p className="mt-1.5 font-display text-[20px] tracking-tight text-gold/90">
          {project.range}
        </p>
      </div>
    </article>
  );
}

/* ---------------- Comparison ---------------- */
function ComparisonSection() {
  const traditional = [
    "£10,000 – £50,000+ projects",
    "Long development cycles",
    "Complex technical language",
    "Enterprise-focused solutions",
    "Expensive agency retainers",
  ];
  const linked = [
    "Focused on solving one business problem at a time",
    "Rapid development using modern tools",
    "Affordable for growing SMEs",
    "Practical outcomes over technical complexity",
    "Clear communication and realistic budgets",
  ];

  return (
    <section
      data-testid="wcwb-comparison"
      className="relative mt-24 sm:mt-36"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="eyebrow text-gold">— A different starting point</span>
          <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-[48px] leading-[1.08] tracking-tight text-mute-100">
            What most people think custom software costs — and what it can actually be.
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-7">
          {/* Traditional */}
          <div className="relative rounded-3xl border border-white/8 bg-white/[0.015] p-7 sm:p-9">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-white/10 bg-white/[0.02] text-mute-300">
                <X className="w-[16px] h-[16px]" />
              </span>
              <span className="text-[11px] uppercase tracking-[0.24em] text-mute-300">
                Traditional software development
              </span>
            </div>
            <ul className="mt-7 space-y-4">
              {traditional.map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-3 text-[15px] leading-[1.65] text-mute-300"
                >
                  <span className="mt-[7px] w-1 h-1 rounded-full bg-mute-300/70 flex-none" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Linked2Marketing */}
          <div className="relative rounded-3xl glass-strong p-7 sm:p-9 overflow-hidden">
            <div
              className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(199,166,106,0.14), transparent 70%)",
              }}
            />
            <div className="relative flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gold/30 bg-gold/[0.06] text-gold">
                <Check className="w-[16px] h-[16px]" />
              </span>
              <span className="text-[11px] uppercase tracking-[0.24em] text-gold">
                What we do
              </span>
            </div>
            <ul className="relative mt-7 space-y-4">
              {linked.map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-3 text-[15px] leading-[1.65] text-mute-100"
                >
                  <span className="mt-[7px] w-1 h-1 rounded-full bg-gold flex-none" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Bottom form: "Could We Build Yours?" ---------------- */
function BottomFormSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business_name: "",
    challenge: "",
    website_url_confirm: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const mountedAtRef = React.useRef(Date.now());
  const submitting = status.state === "submitting";
  const success = status.state === "success";

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e?.target?.value ?? e });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.challenge.trim()) {
      setStatus({
        state: "error",
        message:
          "Please add your name, email, and a short note about what you'd like to solve.",
      });
      return;
    }
    setStatus({ state: "submitting", message: "" });

    // Mirror the home enquiry form's <2s anti-bot guard.
    const elapsed = Date.now() - mountedAtRef.current;
    if (elapsed < 2000) {
      setStatus({
        state: "success",
        message:
          "Thank you — your enquiry has been received. We'll review the details and respond personally.",
      });
      setForm({
        name: "",
        email: "",
        business_name: "",
        challenge: "",
        website_url_confirm: "",
      });
      return;
    }

    try {
      await axios.post(`${API}/strategy-call`, {
        name: form.name,
        email: form.email,
        business_name: form.business_name || null,
        challenge: form.challenge || null,
        source: "what_could_we_build",
        website_url_confirm: form.website_url_confirm || "",
      });
      setStatus({
        state: "success",
        message:
          "Thank you — your enquiry has been received. We'll review the details and respond personally.",
      });
      setForm({
        name: "",
        email: "",
        business_name: "",
        challenge: "",
        website_url_confirm: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? "Please check the form fields."
        : detail || "Something went wrong. Please try again.";
      setStatus({ state: "error", message: msg });
    }
  };

  return (
    <section
      id="could-we-build-yours"
      data-testid="wcwb-bottom-form"
      className="relative mt-24 sm:mt-36 scroll-mt-24"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left column — intro + prompts */}
          <div className="lg:col-span-5">
            <span className="eyebrow text-gold">— Could we build yours?</span>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] tracking-tight text-mute-100">
              Tell us what&rsquo;s driving you mad.
            </h2>
            <p className="mt-6 text-[16px] leading-[1.75] text-mute-200">
              The best systems are not built around technology. They&rsquo;re built around
              frustrations.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              {PAIN_PROMPTS.map((p) => (
                <span
                  key={p}
                  data-testid={`wcwb-prompt-${p
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.02] px-3.5 py-1.5 text-[12.5px] tracking-[0.01em] text-mute-200"
                >
                  {p}?
                </span>
              ))}
            </div>
            <p className="mt-8 text-[14.5px] leading-[1.7] text-mute-300 max-w-md">
              Tell us the problem. We&rsquo;ll tell you, honestly, if it can be automated,
              simplified, or transformed into a custom solution — and roughly what it would cost.
            </p>
          </div>

          {/* Right column — form */}
          <div className="lg:col-span-7">
            {success ? (
              <div
                data-testid="wcwb-form-success"
                className="rounded-3xl glass-strong p-9 sm:p-11"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-gold/30 bg-gold/[0.06] text-gold">
                    <CheckCircle2 className="w-5 h-5" />
                  </span>
                  <span className="eyebrow text-gold">— Received</span>
                </div>
                <h3 className="mt-6 font-display text-3xl text-mute-100">
                  Thank you — we&rsquo;ll be in touch.
                </h3>
                <p className="mt-4 text-[15px] leading-[1.7] text-mute-200 max-w-lg">
                  {status.message}
                </p>
                <button
                  type="button"
                  onClick={() =>
                    setStatus({ state: "idle", message: "" })
                  }
                  data-testid="wcwb-form-reset"
                  className="mt-7 inline-flex items-center gap-2 text-[13px] text-mute-300 hover:text-gold"
                >
                  Send another enquiry
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="wcwb-form"
                className="relative rounded-3xl glass p-7 sm:p-10 overflow-hidden"
              >
                {/* Honeypot — off-screen, never seen by humans */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-9999px",
                    top: "-9999px",
                    height: 0,
                    width: 0,
                    overflow: "hidden",
                  }}
                >
                  <label htmlFor="wcwb_website_url_confirm">
                    Do not fill this field
                  </label>
                  <input
                    id="wcwb_website_url_confirm"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website_url_confirm}
                    onChange={update("website_url_confirm")}
                  />
                </div>

                <div
                  className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(closest-side, rgba(199,166,106,0.12), transparent 70%)",
                  }}
                />

                <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <FormField label="Name" required>
                    <input
                      type="text"
                      data-testid="wcwb-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your full name"
                      className="form-input"
                    />
                  </FormField>
                  <FormField label="Email" required>
                    <input
                      type="email"
                      data-testid="wcwb-email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@business.com"
                      className="form-input"
                    />
                  </FormField>
                  <div className="sm:col-span-2">
                    <FormField label="Business / brand">
                      <input
                        type="text"
                        data-testid="wcwb-business"
                        value={form.business_name}
                        onChange={update("business_name")}
                        placeholder="Company or brand"
                        className="form-input"
                      />
                    </FormField>
                  </div>
                  <div className="sm:col-span-2">
                    <FormField label="Here's what my business is struggling with…" required>
                      <textarea
                        data-testid="wcwb-challenge"
                        value={form.challenge}
                        onChange={update("challenge")}
                        rows={5}
                        placeholder="Describe the frustration, the bottleneck, or the manual process you'd love to simplify."
                        className="form-input resize-none"
                      />
                    </FormField>
                  </div>
                </div>

                <div className="relative mt-8 flex items-center justify-between gap-4 flex-wrap">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="wcwb-submit"
                    className="btn-primary disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Book a free discovery call"}
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                  <span className="text-[12px] text-mute-300">
                    Replies usually within one business day.
                  </span>
                </div>

                <p
                  className="relative mt-4 text-[12px] leading-[1.6] text-mute-300"
                  data-testid="wcwb-privacy"
                >
                  By sending this enquiry, you agree to our{" "}
                  <Link
                    to="/privacy"
                    className="text-mute-200 underline-offset-4 hover:text-gold hover:underline"
                  >
                    Privacy Policy
                  </Link>
                  . We use your details only to respond — never for marketing lists or shared with
                  third parties.
                </p>

                {status.state === "error" && (
                  <div
                    data-testid="wcwb-form-error"
                    className="relative mt-6 rounded-xl border border-red-400/30 bg-red-500/[0.07] p-4 flex items-start gap-3 text-red-300"
                  >
                    <AlertCircle className="w-5 h-5 flex-none" />
                    <p className="text-sm">{status.message}</p>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Local form-input style — keeps this self-contained without polluting global CSS. */}
      <style>{`
        .form-input {
          width: 100%;
          background: rgba(255,255,255,0.025);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 12px;
          color: #ebe8e0;
          padding: 12px 14px;
          font-size: 14.5px;
          line-height: 1.5;
          transition: border-color .25s ease, background .25s ease;
        }
        .form-input::placeholder { color: #94949c; }
        .form-input:focus {
          outline: none;
          border-color: rgba(199,166,106,0.45);
          background: rgba(255,255,255,0.035);
        }
      `}</style>
    </section>
  );
}

function FormField({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[12.5px] tracking-[0.01em] text-mute-200">
        {label}
        {required && <span className="text-gold/80 ml-1">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
